// Upload: Dateien, ganze Ordner (Struktur bleibt als Ordner, optional je Ordner
// eine Kategorie „Eltern - Ordner“), ZIP und mAirList-.mmd. Duplikate werden
// übersprungen. Jede Datei geht einzeln hoch, damit große Mengen stabil laufen.

import { useEffect, useRef, useState } from "react";
import { api, useLoad } from "../../api";
import { Field, Modal, Switch } from "../../ui";

const AUDIO = /\.(mp3|flac|wav|m4a|aac|ogg|opus|aif|aiff|wma)$/i;

interface Item { file: File; path: string; state: "wartet" | "lädt" | "neu" | "Duplikat" | "Fehler" | "zugeordnet"; note?: string }

/** Collect files from a drop, including folders (webkitGetAsEntry). */
async function filesFromDrop(dt: DataTransfer): Promise<{ file: File; path: string }[]> {
  const out: { file: File; path: string }[] = [];
  const readEntry = async (entry: any, dir: string): Promise<void> => {
    if (entry.isFile) {
      const file: File = await new Promise((res, rej) => entry.file(res, rej));
      out.push({ file, path: dir });
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const name = dir ? `${dir}/${entry.name}` : entry.name;
      // readEntries returns batches; call until empty.
      for (;;) {
        const batch: any[] = await new Promise((res, rej) => reader.readEntries(res, rej));
        if (!batch.length) break;
        for (const e of batch) await readEntry(e, name);
      }
    }
  };
  const entries = Array.from(dt.items).map((i) => (i as any).webkitGetAsEntry?.()).filter(Boolean);
  if (entries.length) {
    for (const e of entries) await readEntry(e, "");
  } else {
    for (const f of Array.from(dt.files)) out.push({ file: f, path: "" });
  }
  return out;
}

/** Upload dialog; from the library tree it opens preset for the open folder (category, kind, owner) and with dropped files. */
export function Upload({ onClose, folderId, categoryId, kind: kind0, owner: owner0, files, title }: { onClose: (changed: boolean) => void; folderId?: string | null; categoryId?: string | null; kind?: string; owner?: string; files?: File[]; title?: string }) {
  const { data: cats } = useLoad<any[]>("/api/categories");
  const { data: folders } = useLoad<any[]>("/api/folders");
  const [items, setItems] = useState<Item[]>([]);
  const [folder, setFolder] = useState<string>(folderId ?? "");
  const [category, setCategory] = useState(categoryId ?? "");
  const [kind, setKind] = useState(kind0 ?? "music");
  const [owner, setOwner] = useState(owner0 ?? "auto");
  const [makeCats, setMakeCats] = useState(true);
  const [prefix, setPrefix] = useState("");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [url, setUrl] = useState("");
  const [urlMsg, setUrlMsg] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const dirInput = useRef<HTMLInputElement>(null);

  const add = (list: { file: File; path: string }[]) => {
    const keep = list.filter(({ file }) => AUDIO.test(file.name) || /\.(mmd|zip)$/i.test(file.name));
    setItems((cur) => [...cur, ...keep.map(({ file, path }) => ({ file, path, state: "wartet" as const }))]);
  };
  useEffect(() => { if (files?.length) add(files.map((file) => ({ file, path: "" }))); }, []);
  const fromInput = (fl: FileList | null) => {
    if (!fl) return;
    add(Array.from(fl).map((f) => {
      const rel = (f as any).webkitRelativePath as string | undefined;
      return { file: f, path: rel ? rel.split("/").slice(0, -1).join("/") : "" };
    }));
  };

  const start = async () => {
    setBusy(true);
    // Audio and ZIP first, .mmd afterwards (they attach to songs uploaded before).
    const order = items.map((it, i) => ({ it, i })).sort((a, b) => Number(/\.mmd$/i.test(a.it.file.name)) - Number(/\.mmd$/i.test(b.it.file.name)));
    for (const { it, i } of order) {
      if (it.state !== "wartet") continue;
      setItems((cur) => cur.map((x, j) => (j === i ? { ...x, state: "lädt" } : x)));
      const qs = new URLSearchParams({ name: it.file.name, kind, ...(owner !== "auto" ? { owner } : {}) });
      if (folder) qs.set("folder", folder);
      if (category) qs.set("category", category);
      if (it.path) {
        qs.set("path", it.path);
        if (makeCats) {
          qs.set("make_categories", "1");
          if (prefix.trim()) qs.set("prefix", prefix.trim());
        }
      } else if (/\.zip$/i.test(it.file.name) && makeCats) qs.set("make_categories", "1");
      try {
        const r = await fetch(`/api/library/upload?${qs}`, { method: "POST", body: it.file, headers: { "content-type": "application/x-radiocast-upload" }, credentials: "same-origin" });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error ?? `Fehler ${r.status}`);
        let state: Item["state"] = "neu";
        let note = "";
        if (d.zip) {
          const res: any[] = d.results ?? [];
          note = `${res.filter((x) => !x.duplicate && !x.mmd).length} neu, ${res.filter((x) => x.duplicate).length} Duplikate, ${res.filter((x) => x.mmd && x.matched).length} .mmd`;
        } else if (d.mmd) {
          state = d.matched ? "zugeordnet" : "Fehler";
          note = d.matched ? `Cues: ${(d.marks ?? []).length}` : d.error;
        } else if (d.duplicate) {
          state = "Duplikat";
          note = `schon vorhanden: ${d.artist ? `${d.artist} – ` : ""}${d.title}`;
        }
        setItems((cur) => cur.map((x, j) => (j === i ? { ...x, state, note } : x)));
      } catch (e: any) {
        setItems((cur) => cur.map((x, j) => (j === i ? { ...x, state: "Fehler", note: e.message } : x)));
      }
    }
    setBusy(false);
  };

  const importUrl = async (feed: boolean) => {
    setBusy(true);
    setUrlMsg(feed ? "Feed wird geladen …" : "Datei wird geladen …");
    try {
      const r = await api(feed ? "/api/library/import-feed" : "/api/library/import-url", { body: { url: url.trim(), folder: folder || null, category: category || null, kind: feed && kind === "music" ? "spoken" : kind } });
      if (feed) {
        const res: any[] = r.results ?? [];
        setUrlMsg(`${res.filter((x) => !x.error && !x.duplicate).length} neu, ${res.filter((x) => x.duplicate).length} Duplikate, ${res.filter((x) => x.error).length} Fehler`);
      } else setUrlMsg(r.duplicate ? `Schon vorhanden: ${r.title}` : `Hinzugefügt: ${r.title}`);
      setUrl("");
      setDoneByUrl(true);
    } catch (e: any) {
      setUrlMsg(e.message);
    } finally {
      setBusy(false);
    }
  };
  const [doneByUrl, setDoneByUrl] = useState(false);

  const hasFolders = items.some((i) => i.path);
  const done = items.length > 0 && items.every((i) => i.state !== "wartet" && i.state !== "lädt");
  const counts = items.reduce((acc: Record<string, number>, i) => ({ ...acc, [i.state]: (acc[i.state] ?? 0) + 1 }), {});
  return (
    <Modal title={title ?? "Musik hinzufügen"} onClose={() => !busy && onClose(done || doneByUrl)}>
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={async (e) => { e.preventDefault(); setOver(false); add(await filesFromDrop(e.dataTransfer)); }}
        style={{ border: `2px dashed ${over ? "var(--accent)" : "var(--line)"}`, borderRadius: 10, padding: 22, textAlign: "center", background: over ? "var(--accent-2)" : "#fbfaf6" }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>Dateien oder Ordner hierher ziehen</div>
        <div className="muted small">MP3, FLAC, WAV, M4A/AAC, Ogg · ZIP · mAirList-.mmd (Cues werden übernommen)</div>
        <div className="row" style={{ justifyContent: "center", marginTop: 10 }}>
          <button className="btn" onClick={() => fileInput.current?.click()}>Dateien wählen</button>
          <button className="btn" onClick={() => dirInput.current?.click()}>Ordner wählen</button>
        </div>
        <input ref={fileInput} type="file" multiple style={{ display: "none" }} accept=".mp3,.flac,.wav,.m4a,.aac,.ogg,.opus,.aif,.aiff,.wma,.zip,.mmd" onChange={(e) => { fromInput(e.target.files); e.target.value = ""; }} />
        <input ref={dirInput} type="file" multiple style={{ display: "none" }} {...({ webkitdirectory: "", directory: "" } as any)} onChange={(e) => { fromInput(e.target.files); e.target.value = ""; }} />
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <input type="url" placeholder="oder Adresse einer Audiodatei bzw. eines Podcast-/RSS-Feeds" value={url} onChange={(e) => setUrl(e.target.value)} />
        <button className="btn" disabled={busy || !url.trim()} onClick={() => importUrl(false)}>Datei laden</button>
        <button className="btn" disabled={busy || !url.trim()} onClick={() => importUrl(true)} title="Lädt die neuesten 10 Folgen">Feed laden</button>
      </div>
      {urlMsg && <div className="small muted" style={{ marginTop: 4 }}>{urlMsg}</div>}

      <div className="form" style={{ marginTop: 14 }}>
        <Field label="In Ordner (Verknüpfung)">
          <select value={folder} onChange={(e) => setFolder(e.target.value)}>
            <option value="">– kein Ordner –</option>
            {(folders ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="In Kategorie" hint="leer = Standard aus den Bibliotheks-Einstellungen">
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">–</option>
            {(cats ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Art">
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="music">Musik</option><option value="jingle">Jingle</option><option value="dropper">Dropper</option><option value="station_id">Station-ID</option><option value="imaging">Sweeper/Imaging</option>
            <option value="ad">Werbung</option><option value="news_bed">Nachrichtenbett</option><option value="spoken">Wort</option>
          </select>
        </Field>
        <Field label="Gehört zu" hint="Musik gehört normalerweise allen Sendern, Jingles/Dropper/Sweeper/IDs dem gerade gewählten Sender.">
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="auto">automatisch nach Art</option><option value="shared">allen Sendern</option><option value="station">nur diesem Sender</option>
          </select>
        </Field>
        {(hasFolders || items.some((i) => /\.zip$/i.test(i.file.name))) && (
          <>
            <Field label="Ordner → Kategorien"><Switch checked={makeCats} onChange={setMakeCats} label="je Unterordner eine Kategorie anlegen" /></Field>
            {makeCats && hasFolders && <Field label="Präfix für Kategorienamen" hint="leer = Name des übergeordneten Ordners, z. B. „Urban - Gold“"><input type="text" value={prefix} onChange={(e) => setPrefix(e.target.value)} /></Field>}
          </>
        )}
      </div>

      {items.length > 0 && (
        <div style={{ marginTop: 14, maxHeight: 260, overflowY: "auto" }}>
          <div className="small muted" style={{ marginBottom: 4 }}>
            {items.length} Dateien · {Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(" · ")}
          </div>
          <table className="list">
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="small">{it.path && <span className="muted">{it.path}/</span>}{it.file.name}</td>
                  <td className="small muted">{(it.file.size / 1048576).toFixed(1)} MB</td>
                  <td>
                    <span className={`badge ${it.state === "neu" || it.state === "zugeordnet" ? "ok" : it.state === "Fehler" ? "bad" : it.state === "Duplikat" ? "warn" : ""}`}>{it.state}</span>
                    {it.note && <div className="small muted">{it.note}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="row" style={{ marginTop: 14 }}>
        <span className="small muted">Neue Songs werden danach im Hintergrund analysiert (Dauer, Lautheit, Cues, Cover).</span>
        <span className="spacer" />
        {!done && <button className="btn" disabled={busy} onClick={() => setItems([])}>Leeren</button>}
        {done ? <button className="btn primary" onClick={() => onClose(true)}>Fertig</button> : <button className="btn primary" disabled={busy || !items.length} onClick={start}>{busy ? "Lädt hoch …" : `${items.length} Dateien hochladen`}</button>}
      </div>
    </Modal>
  );
}
